/**
 * Proactive MetaShip-token refresh — a tenant app is used all day, so the
 * shell can't wait for a 401 to notice the 24h token is about to expire.
 * Ported from `modules/legacy-client/src/services/ApiV3.js:96-120`
 * (`REFRESH_LEAD_MS` / the proactive-refresh check) — that file refreshes
 * per-request via an axios interceptor; this shell has no interceptor chain
 * (plain `fetch` calls in `auth/session.ts`), so the same lead-time decision
 * is instead driven by a scheduled timer + a `visibilitychange` catch-up (a
 * background tab's `setTimeout` is throttled/paused by the browser, so the
 * timer alone can't be trusted to fire on time).
 */
import { Auth0InvalidGrantError } from "./auth0";

/** Refresh when less than this much of the token's TTL remains — mirrors ApiV3.js's `REFRESH_LEAD_MS`. */
export const REFRESH_LEAD_MS = 2 * 60 * 1000;
/** Backoff between retries after a TRANSIENT refresh failure (network blip, Auth0 5xx). */
export const RETRY_DELAY_MS = 30 * 1000;
/** Give up (and sign out) after this many consecutive transient failures. */
export const MAX_RETRY_ATTEMPTS = 5;

export type RefreshDecision = { action: "wait"; delayMs: number } | { action: "refresh" };

/**
 * Pure scheduling decision for the HAPPY path: given "now" and the current
 * token's expiry, should we refresh immediately or wait `delayMs`? An
 * unknown expiry (`null`) is NOT treated as "give up" — it attempts a
 * refresh, same as being past the lead window; only a FAILED refresh (see
 * `decideRefreshFailure`) can end the session.
 */
export function decideRefresh(
  nowMs: number,
  expiryMs: number | null,
  leadMs: number = REFRESH_LEAD_MS,
): RefreshDecision {
  if (expiryMs == null) return { action: "refresh" };
  const remaining = expiryMs - nowMs;
  if (remaining <= leadMs) return { action: "refresh" };
  return { action: "wait", delayMs: remaining - leadMs };
}

export type RefreshFailureAction = { action: "retry"; delayMs: number } | { action: "signOut"; clearRefreshToken: boolean };

export interface RefreshFailureParams {
  /** Was the failure Auth0 EXPLICITLY rejecting the refresh token (`invalid_grant`)? */
  isAuthRejected: boolean;
  expiryMs: number | null;
  nowMs: number;
  /** 1-based count of consecutive failures, including this one. */
  attempt: number;
}

/**
 * Pure decision for a FAILED refresh attempt. Only an Auth0-rejected token,
 * or a token that has actually expired, ends the session — a transient
 * failure (network blip, Auth0 5xx) with time still on the clock retries
 * instead. The stored refresh token is only ever discarded when Auth0 itself
 * rejected it; every other terminal case keeps it, so the NEXT page load's
 * `resumeFromRefreshToken()` can still succeed if the failures really were
 * transient.
 */
export function decideRefreshFailure(params: RefreshFailureParams): RefreshFailureAction {
  if (params.isAuthRejected) return { action: "signOut", clearRefreshToken: true };
  const expired = params.expiryMs == null || params.expiryMs <= params.nowMs;
  if (expired) return { action: "signOut", clearRefreshToken: false };
  if (params.attempt >= MAX_RETRY_ATTEMPTS) return { action: "signOut", clearRefreshToken: false };
  return { action: "retry", delayMs: RETRY_DELAY_MS };
}

export interface RefreshSchedulerDeps {
  /** The active session's current token expiry, or `null` if there is none. */
  getExpiryMs: () => number | null;
  /** Perform the actual refresh (Auth0 refresh-token → re-mint org token → republish). */
  refresh: () => Promise<void>;
  signOut: (clearRefreshToken: boolean) => void;
  now?: () => number;
}

/** Schedules `deps.refresh()` ~`REFRESH_LEAD_MS` before the current token expires, with bounded retry on failure. */
export class RefreshScheduler {
  private deps: Required<RefreshSchedulerDeps>;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private started = false;
  private retryAttempt = 0;

  constructor(deps: RefreshSchedulerDeps) {
    this.deps = { now: () => Date.now(), ...deps };
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.scheduleNext();
  }

  stop(): void {
    this.started = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    // A throttled background tab's timer can fire late (or, on some
    // browsers, effectively never until refocus) — re-evaluate the moment
    // the tab becomes visible again rather than trusting the stale timer.
    if (document.visibilityState === "visible") this.scheduleNext();
  };

  private scheduleNext(): void {
    if (!this.started) return;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const decision = decideRefresh(this.deps.now(), this.deps.getExpiryMs());
    if (decision.action === "refresh") {
      void this.runRefresh();
      return;
    }
    this.timer = setTimeout(() => void this.runRefresh(), decision.delayMs);
  }

  private async runRefresh(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      await this.deps.refresh();
      this.retryAttempt = 0;
      this.scheduleNext();
    } catch (e) {
      this.retryAttempt += 1;
      const decision = decideRefreshFailure({
        isAuthRejected: e instanceof Auth0InvalidGrantError,
        expiryMs: this.deps.getExpiryMs(),
        nowMs: this.deps.now(),
        attempt: this.retryAttempt,
      });
      if (decision.action === "signOut") {
        this.deps.signOut(decision.clearRefreshToken);
      } else if (this.started) {
        this.timer = setTimeout(() => void this.runRefresh(), decision.delayMs);
      }
    } finally {
      this.running = false;
    }
  }
}
